import { Component, ReactNode, ErrorInfo } from "react";
import * as Sentry from "@sentry/react";
import { captureException as posthogCaptureException, captureEvent } from "@/lib/posthog";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface AppErrorBoundaryProps {
  children: ReactNode;
  variant: "shell" | "fullpage";
  onReset?: () => void;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  private prevVariant: string;

  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.prevVariant = props.variant;
    this.resetError = this.resetError.bind(this);
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
    posthogCaptureException(error, { componentStack: errorInfo.componentStack });
    captureEvent("modal_shown", { modalKind: "critical_error", trigger: "render_crash" });
  }

  componentDidUpdate() {
    if (this.props.variant !== this.prevVariant) {
      this.prevVariant = this.props.variant;
      if (this.state.hasError) {
        this.setState({ hasError: false, error: null });
      }
    }
  }

  resetError() {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.variant === "shell") {
      const handleTryAgain = () => {
        captureEvent("modal_primary_clicked", { modalKind: "critical_error" });
        this.resetError();
      };

      const handleGoHome = () => {
        captureEvent("modal_secondary_clicked", { modalKind: "critical_error" });
        window.location.href = "/dashboard";
      };

      return (
        <div
          className="h-full flex flex-col items-center justify-center bg-background p-8"
          data-testid="error-boundary-shell"
        >
          <Card className="max-w-md w-full">
            <CardHeader className="text-center gap-2">
              <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle>Something went wrong</CardTitle>
              <CardDescription>
                This part of the app hit a snag. Your data is safe — try again or head back to your dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Button onClick={handleTryAgain} data-testid="button-error-try-again">
                <RefreshCw className="w-4 h-4" />
                Try Again
              </Button>
              <Button variant="outline" onClick={handleGoHome} data-testid="button-error-go-home">
                <Home className="w-4 h-4" />
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    const handleRefresh = () => {
      captureEvent("modal_primary_clicked", { modalKind: "critical_error" });
      window.location.reload();
    };

    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center bg-background p-8"
        data-testid="error-boundary-fullpage"
      >
        <Card className="max-w-md w-full">
          <CardHeader className="text-center gap-2">
            <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Something went wrong</CardTitle>
            <CardDescription>
              An unexpected error occurred. Please try refreshing the page.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button onClick={handleRefresh} data-testid="button-error-refresh">
              <RefreshCw className="w-4 h-4" />
              Refresh Page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
}
